// ./src/components/features/Dashboard/DashboardSection/DashboardSection.tsx
// Reusable section wrapper for dashboard areas (Tasks, Expiring Documents, Upcoming Services).
// Renders a section header with title, count badges, action link, optional "Add" tile,
// children tiles, and expand/collapse for overflow items.

import React, { useState, useCallback, useMemo } from "react";
import { Stack, Group, Text, Badge, Box, Collapse } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppButton } from "@/components/generic/AppButton";

import { DashboardItemTileSkeleton } from "./DashboardItemTile";

// ============================================
// Types
// ============================================

/**
 * Semantic count badge types — section determines colors internally.
 */
export type CountBadgeType = "error" | "warning" | "info";

export interface CountBadge {
  type: CountBadgeType;
  count: number;
}

export interface SectionHeaderAction {
  label: string;
  onClick: () => void;
  iconName?: string;
}

export interface SectionAddAction {
  label: string;
  onClick: () => void;
}

export interface SectionEmptyState {
  title?: string;
  description?: string;
  iconName?: string;
}

export interface DashboardSectionProps {
  /** Section title (e.g., "Tasks", "Expiring Documents") */
  title: string;

  /** Count badges displayed next to the title */
  countBadges?: CountBadge[];

  /** Action button/link in the header (e.g., "View All", "View Report") */
  headerAction?: SectionHeaderAction;

  /** Optional dashed "Add" tile at the top of the section */
  addAction?: SectionAddAction;

  /** Section content — typically DashboardItemTile components */
  children?: React.ReactNode;

  /** Whether data is loading */
  loading?: boolean;

  /** Number of skeleton tiles to show while loading (default: 3) */
  skeletonCount?: number;

  /** Custom skeleton renderer — overrides default DashboardItemTileSkeleton */
  renderSkeleton?: () => React.ReactNode;

  /** Empty state config when no children and not loading */
  emptyState?: SectionEmptyState;

  /** Maximum items to show before "Show More" (default: 5, 0 = no limit) */
  initialDisplayCount?: number;

  /** Total item count — used to determine if expand/collapse is needed */
  totalItems?: number;
}

// ============================================
// Constants
// ============================================

const DEFAULT_SKELETON_COUNT = 3;
const DEFAULT_INITIAL_DISPLAY_COUNT = 5;

// ============================================
// Count Badge Color Mapping
// ============================================

const COUNT_BADGE_COLORS: Record<CountBadgeType, string> = {
  error: "red",
  warning: "orange",
  info: "blue",
};

// ============================================
// Sub-components
// ============================================

interface SectionHeaderProps {
  title: string;
  countBadges?: CountBadge[];
  headerAction?: SectionHeaderAction;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  countBadges,
  headerAction,
}) => {
  // Filter out zero-count badges
  const visibleBadges = useMemo(
    () => (countBadges || []).filter((b) => b.count > 0),
    [countBadges]
  );

  return (
    <Group justify="space-between" align="center" wrap="nowrap">
      <Group gap="xs" wrap="nowrap">
        <Text fw={700} size="xl">
          {title}
        </Text>
        {visibleBadges.length > 0 && (
          <Group gap={4} wrap="nowrap">
            {visibleBadges.map((badge, index) => (
              <Badge
                key={`${badge.type}-${index}`}
                color={COUNT_BADGE_COLORS[badge.type]}
                variant="filled"
                size="sm"
                circle
              >
                {badge.count}
              </Badge>
            ))}
          </Group>
        )}
      </Group>

      {headerAction && (
        <Text
          component="button"
          size="sm"
          fw={500}
          c="brand"
          onClick={headerAction.onClick}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
            whiteSpace: "nowrap",
          }}
        >
          {headerAction.label}
        </Text>
      )}
    </Group>
  );
};

interface AddActionTileProps {
  label: string;
  onClick: () => void;
}

const AddActionTile: React.FC<AddActionTileProps> = ({ label, onClick }) => {
  return (
    <Box
      onClick={onClick}
      py="sm"
      px="md"
      style={{
        border: "2px dashed var(--mantine-color-brand-3)",
        borderRadius: "var(--mantine-radius-md)",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background-color 150ms ease, border-color 150ms ease",
      }}
      className="dashboard-add-tile"
    >
      <Group gap={6} wrap="nowrap">
        <Text c="brand" size="sm" fw={500}>
          +
        </Text>
        <Text c="brand" size="sm" fw={500}>
          {label}
        </Text>
      </Group>
    </Box>
  );
};

interface EmptyContentProps {
  emptyState: SectionEmptyState;
}

const EmptyContent: React.FC<EmptyContentProps> = ({ emptyState }) => {
  return (
    <Stack align="center" gap="xs" py="md">
      {emptyState.iconName && (
        <Box c="dimmed">
          <AppButton variant="tertiary" size="lg" iconName={emptyState.iconName} disabled />
        </Box>
      )}
      {emptyState.title && (
        <Text size="sm" c="dimmed" ta="center" fw={500}>
          {emptyState.title}
        </Text>
      )}
      {emptyState.description && (
        <Text size="xs" c="dimmed" ta="center">
          {emptyState.description}
        </Text>
      )}
    </Stack>
  );
};

// ============================================
// Main Component
// ============================================

export const DashboardSection: React.FC<DashboardSectionProps> = ({
  title,
  countBadges,
  headerAction,
  addAction,
  children,
  loading = false,
  skeletonCount = DEFAULT_SKELETON_COUNT,
  renderSkeleton,
  emptyState,
  initialDisplayCount = DEFAULT_INITIAL_DISPLAY_COUNT,
  totalItems = 0,
}) => {
  const { t } = useTranslation(["common"]);

  // Expand/collapse state
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // Determine if we need expand/collapse
  const hasOverflow =
    initialDisplayCount > 0 && totalItems > initialDisplayCount;
  const hiddenCount = hasOverflow ? totalItems - initialDisplayCount : 0;

  // Split children into visible and hidden groups
  const childArray = React.Children.toArray(children);
  const visibleChildren =
    hasOverflow && !isExpanded
      ? childArray.slice(0, initialDisplayCount)
      : childArray;
  const hiddenChildren = hasOverflow ? childArray.slice(initialDisplayCount) : [];

  // Determine if section has content
  const hasChildren = childArray.length > 0;
  const showEmpty = !loading && !hasChildren && emptyState;
  const showContent = !loading && hasChildren;

  return (
    <Stack gap="sm">
      {/* Section Header */}
      <SectionHeader
        title={title}
        countBadges={countBadges}
        headerAction={headerAction}
      />

      {/* Add Action Tile */}
      {addAction && (
        <AddActionTile label={addAction.label} onClick={addAction.onClick} />
      )}

      {/* Loading State */}
      {loading && (
        renderSkeleton ? renderSkeleton() : (
          <Stack gap="sm">
            {Array.from({ length: skeletonCount }).map((_, index) => (
              <DashboardItemTileSkeleton key={`skeleton-${index}`} />
            ))}
          </Stack>
        )
      )}

      {/* Empty State */}
      {showEmpty && <EmptyContent emptyState={emptyState!} />}

      {/* Content - Visible Items */}
      {showContent && (
        <Stack gap="sm">
          {hasOverflow ? visibleChildren.slice(0, initialDisplayCount) : visibleChildren}
        </Stack>
      )}

      {/* Content - Hidden Items (Collapsible) */}
      {showContent && hasOverflow && hiddenChildren.length > 0 && (
        <Collapse in={isExpanded}>
          <Stack gap="sm">
            {hiddenChildren}
          </Stack>
        </Collapse>
      )}

      {/* Show More / Show Less */}
      {showContent && hasOverflow && (
        <AppButton
          variant="tertiary"
          size="xs"
          iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
          iconPosition="right"
          onClick={handleToggleExpand}
        >
          {isExpanded
            ? t("common:dashboard.section.showLess")
            : t("common:dashboard.section.showMore", { count: hiddenCount })}
        </AppButton>
      )}
    </Stack>
  );
};